import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type ReactNode,
} from "react";
import { cn } from "@/lib/cn";

type ToastKind = "info" | "success" | "error";

type ToastItem = {
	id: number;
	message: string;
	kind: ToastKind;
};

interface ToastContextValue {
	toast: (message: string, kind?: ToastKind) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
	const [items, setItems] = useState<ToastItem[]>([]);

	const toast = useCallback((message: string, kind: ToastKind = "info") => {
		const id = nextId++;
		setItems((prev) => [...prev, { id, message, kind }]);
		window.setTimeout(() => {
			setItems((prev) => prev.filter((t) => t.id !== id));
		}, 3200);
	}, []);

	const value = useMemo(() => ({ toast }), [toast]);

	return (
		<ToastContext.Provider value={value}>
			{children}
			<div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2">
				{items.map((item) => (
					<div
						key={item.id}
						className={cn(
							"rounded-[var(--radius-panel)] bg-surface px-3 py-2.5 text-sm text-fg",
							"shadow-[var(--shadow-pop)] ring-1 ring-[var(--ring-hairline)]",
							item.kind === "success" && "border-l-2 border-l-emerald-500",
							item.kind === "error" && "border-l-2 border-l-rose-500",
							item.kind === "info" && "border-l-2 border-l-teal-500",
						)}
					>
						{item.message}
					</div>
				))}
			</div>
		</ToastContext.Provider>
	);
}

export function useToast(): ToastContextValue {
	const ctx = useContext(ToastContext);
	if (!ctx) throw new Error("useToast must be used within ToastProvider");
	return ctx;
}
